import { firebase, db } from '@/firebase.js'
import { firestoreAction } from 'vuexfire'

const getDefaultState = () => {
  return {
    userAuth: {},
    loggedIn: false,
    authMessage: {},
    data: {},
    activeRole: null, // Currently active role for role switching
    availableRoles: [] // All roles available to the user
  }
}

const state = getDefaultState()

const getters = {
  id(state) {
    try {
      return state.data.id
    } catch {
      return null
    }
  },
  name(state) {
    try {
      return state.data.name || ''
    } catch {
      return null
    }
  },
  loggedIn(state) {
    try {
      return state.loggedIn ? true : false
    } catch {
      return false
    }
  },
  // Current active role (for role switching)
  activeRole(state) {
    try {
      return state.activeRole || state.data.role || null
    } catch {
      return null
    }
  },
  // Available roles for the user
  availableRoles(state) {
    try {
      // If user has roles array (new system), use that
      if (state.data.roles && Array.isArray(state.data.roles)) {
        return state.data.roles
      }
      // Otherwise, fall back to single role (legacy system)
      if (state.data.role) {
        return [state.data.role]
      }
      return []
    } catch {
      return []
    }
  },
  // Check if user has multiple roles available
  hasMultipleRoles(state, getters) {
    return getters.availableRoles.length > 1
  },
  isStaff(state, getters) {
    try {
      const role = getters.activeRole
      return getters.loggedIn &&
        (role === 'staff' ||
          role === 'manager' ||
          role === 'admin' ||
          role === 'developer')
        ? true
        : false
    } catch {
      return false
    }
  },
  isManager(state, getters) {
    try {
      const role = getters.activeRole
      return getters.loggedIn &&
        (role === 'manager' || role === 'admin' || role === 'developer')
        ? true
        : false
    } catch {
      return false
    }
  },
  isAdmin(state, getters) {
    try {
      const role = getters.activeRole
      return getters.loggedIn && (role === 'admin' || role === 'developer')
        ? true
        : false
    } catch {
      return false
    }
  },
  isDeveloper(state, getters) {
    try {
      const role = getters.activeRole
      return getters.loggedIn && role === 'developer' ? true : false
    } catch {
      return false
    }
  },
  storeName(state) {
    // TODO: simplify to 'store'
    try {
      return state.data.store
    } catch {
      return null
    }
  },
  stores(state) {
    try {
      return Array.from(state.data.stores)
    } catch {
      return []
    }
  }
}

const actions = {
  bindUser: firestoreAction(({ bindFirestoreRef }, { uid }) => {
    return bindFirestoreRef('data', db.collection('users').doc(uid))
  }),
  unbindUser: firestoreAction(({ unbindFirestoreRef }) => {
    return unbindFirestoreRef('data')
  }),
  async login({ commit }, { email, password }) {
    if (!firebase.auth().currentUser)
      try {
        await firebase.auth().signInWithEmailAndPassword(email, password)
      } catch (error) {
        console.log('error logging in: ', error)
        commit('setAuthMessage', error)
        commit('logout', {})
        return Promise.reject(error)
      }
    commit('setUserAuth', firebase.auth().currentUser)
    commit('resetAuthMessage', null)
    commit('login', null)
    return Promise.resolve
  },
  async logout({ commit }) {
    if (firebase.auth().currentUser)
      try {
        await firebase.auth().signOut()
      } catch (error) {
        commit('setAuthMessage', error)
        return Promise.reject(error)
      }
    commit('setUserAuth', null)
    commit('setAuthMessage', null)
    commit('logout', null)
    return Promise.resolve
  },
  async sendPasswordResetEmail({ commit }, { email }) {
    try {
      await firebase.auth().sendPasswordResetEmail(email)
      return Promise.resolve()
    } catch (error) {
      console.log('error sending password reset email: ', error)
      commit('setAuthMessage', error)
      return Promise.reject(error)
    }
  },
  async updatePrimaryStore({ state, getters, dispatch }, storeName = '') {
    let stores = getters['stores']
    if (!stores)
      return Promise.reject(`'Stores' list not loaded yet for this user.`)
    if (!stores.includes(storeName))
      return Promise.reject(
        `This user does not have access to store '${storeName}'.`
      )
    if (!state.data.id) return Promise.reject('User ID not found.')
    return await dispatch('updateItem', {
      store: storeName
    })
  },
  async updateItem({ state }, data) {
    try {
      return await db.collection('users').doc(state.data.id).update(data)
    } catch {
      return Promise.reject('Updating user data failed.', state.data.id, data)
    }
  },
  resetAuthMessage({ commit }) {
    commit('resetAuthMessage', null)
  },
  resetUserState({ commit }) {
    commit('resetState', null)
  },
  // Role switching actions
  async switchRole({ commit, getters, state }, newRole) {
    const availableRoles = getters.availableRoles

    // Validate that the user has access to this role
    if (!availableRoles.includes(newRole)) {
      return Promise.reject(`User does not have access to role: ${newRole}`)
    }

    // Update active role
    commit('setActiveRole', newRole)

    // Persist to localStorage
    localStorage.setItem('activeRole', newRole)

    return Promise.resolve(newRole)
  },
  initializeActiveRole({ commit, getters, state }) {
    // Try to restore from localStorage first
    const savedRole = localStorage.getItem('activeRole')
    const availableRoles = getters.availableRoles

    if (savedRole && availableRoles.includes(savedRole)) {
      commit('setActiveRole', savedRole)
    } else if (availableRoles.length > 0) {
      // Default to the user's primary role or first available role
      const defaultRole = state.data.role || availableRoles[0]
      commit('setActiveRole', defaultRole)
      localStorage.setItem('activeRole', defaultRole)
    }
  }
}

const mutations = {
  login(state) {
    this._vm.$set(state, 'loggedIn', true)
  },
  logout(state) {
    this._vm.$set(state, 'loggedIn', false)
  },
  setUserAuth(state, userAuth) {
    this._vm.$set(state, 'userAuth', userAuth)
  },
  setAuthMessage(state, message) {
    this._vm.$set(state, 'authMessage', { ...message })
  },
  resetAuthMessage(state) {
    this._vm.$set(state, 'authMessage', {})
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
  setActiveRole(state, role) {
    this._vm.$set(state, 'activeRole', role)
  },
  setAvailableRoles(state, roles) {
    this._vm.$set(state, 'availableRoles', roles)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

// import firebase from 'firebase/app'
// import 'firebase/auth'

// const state = {
//   userAuth: {},
// }

// const mutations = {
//   SET_USER(state, { user }) {
//     state.userAuth = user
//   },
//   UNSET_USER() {
//     state.userAuth = {}
//   },
// }

// const actions = {
//   init({ commit, rootState }) {
//     rootState.app.auth().onAuthStateChanged(user => {
//       if (user) {
//         commit('SET_USER', { user })
//       } else {
//         commit('UNSET_USER')
//       }
//     })
//   },
//   login({ rootState }) {
//     //var authProvider = new firebase.auth.GoogleAuthProvider()
//     //return rootState.app.auth().signInWithPopup(authProvider)
//   },
//   logout({ rootState }) {
//     rootState.app.auth().signOut()
//   },
// }

// export default { namespaced: true, state, mutations, actions }
